import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbSchema, faqSchema } from "@/components/SEO";

export default function TheHouse() {
  const questionWrapRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = questionWrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const p = Math.max(0, Math.min(1, -rect.top / total));
        setProgress(p);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // ── Letter-by-letter animation phases ───────────────────────────
  // 0.00 – 0.40 : letters fly in (alternating L/R), stagger per letter
  // 0.40 – 0.65 : fully assembled — hold
  // 0.65 – 0.90 : fade out
  const questionText = "So what is exceptional?";
  const letters = questionText.split("");
  const totalChars = letters.filter((c) => c !== " ").length;
  let charIdx = -1;

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  let holdOpacity = 1;
  if (progress > 0.65) {
    holdOpacity = Math.max(0, 1 - (progress - 0.65) / 0.25);
  }


  return (
    <div data-testid="page-house" className="pt-32">
      <SEO
        title="The House of Impact — About Next in Luxury"
        description="The House of Impact — discover Next in Luxury: an international luxury advisory house built on savoir-faire, culture and humanistic luxury, guiding brands and leaders toward the exceptional."
        path="/the-house"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "The House of Impact", path: "/the-house" },
          ]),
          faqSchema([
            {
              q: "What does Next in Luxury do?",
              a: "Next in Luxury is a luxury advisory house that designs strategy, transmits savoir-faire and elevates the experience of brands operating in hospitality, retail, culture, tourism and exceptional experiences.",
            },
            {
              q: "What makes Next in Luxury different?",
              a: "Next in Luxury combines heritage & culture, savoir-faire & mastery, humanistic luxury and vision & innovation into a single advisory practice — built to make excellence timeless.",
            },
          ]),
        ]}
      />
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2 md:sticky md:top-32 md:self-start">
          <p className="font-eyebrow opacity-60">Chapter N°01</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <h1
              className="title-caps leading-[0.9]"
              style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            >
              THE HOUSE
              <br />
              <span className="font-script" style={{ color: "#8C6B3F", textTransform: "none", letterSpacing: 0, fontSize: "1.2em" }}>
                of Impact.
              </span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Lead image */}
      <div className="w-full h-[70vh] my-16 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1737920459846-2d0318700658?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxlZGl0b3JpYWwlMjBqb3VybmFsJTIwZGVzayUyMHN0aWxsJTIwbGlmZSUyMGx1eHVyeXxlbnwwfHx8fDE3ODQxNjgxNzN8MA&ixlib=rb-4.1.0&q=85"
          alt="Editorial still life"
          className="w-full h-full object-cover duotone"
        />
      </div>

      {/* Lead paragraph */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-4 md:sticky md:top-32 md:self-start">
          <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
            The Belief
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal>
            <h2
              className="title-caps leading-[1.02]"
              style={{
                fontSize: "clamp(2rem, 4.4vw, 4.25rem)",
                letterSpacing: "-0.01em",
              }}
            >
              An execution-first firm built to keep you
              <br />
              <span
                className="font-script"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  fontSize: "1.25em",
                  color: "#8C6B3F",
                }}
              >
                at the top.
              </span>
            </h2>
            <p className="font-body mt-12 opacity-85 text-lg leading-relaxed">
              Next in Luxury is a House of Impact for luxury brands ready
              to go beyond excellence.
            </p>
            <p className="font-body mt-6 opacity-80 text-lg leading-relaxed">
              Luxury is moving fast. Clients are changing, expectations are
              rising, and the old codes are no longer enough. Yet many
              brands are still too slow to turn ambition into daily
              reality. We were born to close that gap.
            </p>
            <p className="font-body mt-6 opacity-80 text-lg leading-relaxed">
              We believe the top is not a place. It is a choice few are
              ready to make.
            </p>
            <p className="font-body mt-6 opacity-80 text-lg leading-relaxed">
              In luxury, excellence is expected: beautiful products,
              refined service, trained teams, and strong standards. But
              exceptionality asks for more. It requires soul and system,
              culture and discipline, vision and execution working as one.
            </p>
            <p className="font-body mt-6 opacity-80 text-lg leading-relaxed">
              We are an execution-first consulting firm bringing together
              leading luxury experts with deep on-the-ground experience
              across strategy, customer experience, training, operations,
              and implementation. We do not leave strategy on slides. We
              bring it to the ground, where it becomes behavior, service,
              rituals, standards, and experiences people can truly feel.
            </p>
            <p className="font-body mt-6 opacity-80 text-lg leading-relaxed">
              Our mission is to help luxury brands build the culture,
              capabilities, and human intelligence required to move from
              excellent to exceptional and become the next reference in
              luxury.
            </p>
            <Link
              to="/private-inquiry"
              className="btn-ghost mt-14 inline-flex"
              data-testid="house-hero-cta"
            >
              Be the Next One <span className="arrow">⟶</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══ EXCEPTIONAL — Scroll-fade question + editorial reveal ═══ */}
      {/* Part 1 — Full-viewport pinned question that fades out on scroll */}
      <section
        ref={questionWrapRef}
        data-testid="house-exceptional-question"
        className="relative"
        style={{ background: "#14140F", height: "220vh" }}
      >
        <div
          className="sticky top-0 flex items-center justify-center overflow-hidden"
          style={{ height: "100vh" }}
        >
          {/* soft radial vignette */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 55% at 50% 50%, rgba(140,107,63,0.08) 0%, rgba(20,20,15,0) 70%)",
            }}
          />
          <h2
            aria-label={questionText}
            className="font-script text-center px-6"
            style={{
              color: "#8C6B3F",
              textTransform: "none",
              letterSpacing: 0,
              fontSize: "clamp(3rem, 9vw, 9rem)",
              lineHeight: 1.05,
              opacity: holdOpacity,
              transition: "opacity 200ms linear",
              willChange: "opacity",
            }}
          >
            {letters.map((ch, i) => {
              if (ch === " ") {
                return (
                  <span key={i} aria-hidden style={{ display: "inline-block", width: "0.35em" }}>
                    &nbsp;
                  </span>
                );
              }
              charIdx += 1;
              const stagger = charIdx / Math.max(1, totalChars - 1);
              // Each letter completes its entrance within a window of progress [0, 0.40]
              // Stagger start per letter
              const start = stagger * 0.22;
              const end = start + 0.18;
              const rawT = (progress - start) / (end - start);
              const t = Math.max(0, Math.min(1, rawT));
              const eased = easeOut(t);
              const fromLeft = charIdx % 2 === 0;
              const distance = 60; // vw travel
              const tx = (1 - eased) * distance * (fromLeft ? -1 : 1);
              return (
                <span
                  key={i}
                  aria-hidden
                  style={{
                    display: "inline-block",
                    transform: `translate3d(${tx}vw, 0, 0)`,
                    opacity: eased,
                    willChange: "transform, opacity",
                  }}
                >
                  {ch}
                </span>
              );
            })}
          </h2>
        </div>
      </section>

      {/* Part 2 — Two-column editorial answer */}
      <section
        data-testid="house-exceptional-answer"
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-12 gap-10 md:gap-16 items-center"
      >
        {/* Left column — copy */}
        <div className="col-span-12 md:col-span-6">
          <Reveal>
            <p className="font-eyebrow mb-8" style={{ color: "#8C6B3F" }}>
              The Answer
            </p>
            <p className="font-body opacity-85 text-lg leading-relaxed">
              It is what happens when a brand's ambition becomes culture,
              when standards become instinct, and when every gesture carries
              meaning. It is not only how beautifully a brand performs, but
              how deeply it transforms the people who deliver it, the
              clients who experience it, and the business that grows from
              it.
            </p>
            <p className="font-body opacity-80 mt-6 text-lg leading-relaxed">
              At Next in Luxury, we help brands move from excellent to
              exceptional through{" "}
              <span className="font-serif-italic">Humanistic Luxury™</span>{" "}
              — our methodology that connects culture and leadership,
              strategy, experience design and operational mastery to build
              luxury that is human, intentional, consistent and alive.
            </p>
          </Reveal>
        </div>

        {/* Right column — overlapping editorial diptych */}
        <div className="col-span-12 md:col-span-6">
          <Reveal delay={180}>
            <div
              className="relative mx-auto"
              style={{ maxWidth: "560px", aspectRatio: "1 / 1.05" }}
            >
              {/* Larger image — behind, top-left */}
              <div
                className="absolute overflow-hidden"
                style={{
                  top: "0",
                  left: "0",
                  width: "72%",
                  aspectRatio: "3 / 4",
                  transform: "rotate(-1.5deg)",
                  boxShadow: "0 24px 60px -20px rgba(20,20,15,0.35)",
                  zIndex: 1,
                }}
              >
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/vmlh91pc_Screenshot%202026-07-20%20at%205.20.16%E2%80%AFpm.png"
                  alt="Craftsmanship in service"
                  className="w-full h-full object-cover"
                  style={{
                    filter:
                      "grayscale(1) contrast(1.15) brightness(0.94) sepia(0.05)",
                  }}
                />
              </div>

              {/* Smaller image — in front, bottom-right */}
              <div
                className="absolute overflow-hidden"
                style={{
                  bottom: "0",
                  right: "0",
                  width: "56%",
                  aspectRatio: "3 / 4",
                  transform: "rotate(2deg)",
                  boxShadow: "0 30px 70px -22px rgba(20,20,15,0.45)",
                  zIndex: 2,
                }}
              >
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/2qh61n2s_Screenshot%202026-07-20%20at%205.20.10%E2%80%AFpm.png"
                  alt="Archival heritage"
                  className="w-full h-full object-cover"
                  style={{
                    filter:
                      "grayscale(1) contrast(1.15) brightness(0.94) sepia(0.05)",
                  }}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      {/* ═══ END EXCEPTIONAL ═══ */}

      {/* Pull quote */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-40 grid grid-cols-12">
        <div className="col-span-12 md:col-span-8 md:col-start-3">
          <Reveal>
            <p
              className="font-serif-italic leading-[1.1]"
              style={{ fontSize: "clamp(2rem, 4vw, 4rem)" }}
            >
              “We do not leave strategy on slides. We bring it to the ground —
              where it becomes behaviour, service, rituals, standards and
              moments people can feel.”
            </p>
          </Reveal>
        </div>
      </section>

      {/* Two paths — Consulting / Training (as disciplines, not services) */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              Two Disciplines
            </p>
          </div>
          <div className="col-span-12 md:col-span-10 grid grid-cols-1 md:grid-cols-2 gap-16">
            <Reveal>
              <div className="hairline mb-8" />
              <p className="font-eyebrow opacity-60">Advisory</p>
              <h3
                className="title-caps mt-6 leading-[0.95]"
                style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)" }}
              >
                STRATEGY THAT
                <br />
                <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.4em", color: "#8C6B3F" }}>
                  becomes reality.
                </span>
              </h3>
              <p className="font-body opacity-75 mt-6">
                We define direction and embed it into the daily reality of the
                business. Strategies that live in the boardroom rarely survive
                on the floor — ours are engineered to.
              </p>
            </Reveal>
            <Reveal delay={150}>
              <div className="hairline mb-8" />
              <p className="font-eyebrow opacity-60">Transmission</p>
              <h3
                className="title-caps mt-6 leading-[0.95]"
                style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)" }}
              >
                SAVOIR-FAIRE THAT
                <br />
                <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.4em", color: "#8C6B3F" }}>
                  outlasts leadership.
                </span>
              </h3>
              <p className="font-body opacity-75 mt-6">
                We transmit the culture, capabilities and human intelligence
                required to become the next reference — through programs designed
                for leaders, teams and institutions.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Continue */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 flex justify-between items-end">
        <div>
          <p className="font-eyebrow opacity-60">Continue</p>
          <h2 className="font-serif-display mt-4" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            <span className="font-serif-italic">The Manifesto</span>
          </h2>
        </div>
        <Link to="/manifesto" className="btn-ghost" data-testid="house-continue-link">
          Chapter N°02 <span className="arrow">→</span>
        </Link>
      </section>
    </div>
  );
}
